import React, { useRef, useEffect, useState, useMemo } from 'react';
import {
  Droplet,
  AlertTriangle,
  Clock,
  User,
  Phone,
  Mail,
  MapPin,
  Building2,
  Home,
  FileText,
  Wrench,
  Calendar,
  DoorOpen,
  Copy,
  Check,
  Sparkles,
  AlertCircle,
  X,
} from 'lucide-react';
import PreferredPartnerCallout from './PreferredPartnerCallout';
import JobOrderReviewModal from './JobOrderReviewModal';
import {
  buildJobOrderFromMetaData,
  getClassifiedResponsePartner,
} from '../../utils/maintenanceTicketJobOrderUtils';

async function copyTextToClipboard(text) {
  const t = text == null ? '' : String(text);
  if (!t) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(t);
      return true;
    }
  } catch {
    /* fall through */
  }
  try {
    const ta = document.createElement('textarea');
    ta.value = t;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    ta.style.top = '0';
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

function CopyButton({ value, className = '' }) {
  const [copied, setCopied] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (value == null || value === '') return;
    const ok = await copyTextToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const stopMouseDown = (e) => {
    e.stopPropagation();
  };

  if (value == null || value === '') return null;

  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setShowTooltip(true)}
      onMouseLeave={() => setShowTooltip(false)}
      onFocus={() => setShowTooltip(true)}
      onBlur={() => setShowTooltip(false)}
      tabIndex={-1}
    >
      {/* Tooltip (shown underneath the button) */}
      {showTooltip && !copied && (
        <div
          className="absolute left-1/2 -translate-x-1/2 top-full mt-0.5 px-2 py-1 rounded bg-black text-white text-xs font-medium shadow z-50 pointer-events-none select-none"
          style={{ whiteSpace: "nowrap" }}
        >
          Copy
        </div>
      )}
 
      <button
        type="button"
        onMouseDown={stopMouseDown}
        onPointerDown={stopMouseDown}
        onClick={handleCopy}
        className={`p-1.5 rounded-md hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-900 ${className}`}
        aria-label="Copy to clipboard"
      >
        {copied ? (
          <Check className="size-4 text-green-600" strokeWidth={2} />
        ) : (
          <Copy className="size-4" strokeWidth={2} />
        )}
      </button>
    </div>
  );
}

function FieldRow({
  label,
  value,
  icon: Icon,
  isMissing = false,
  isInferred = false,
  multiline = false,
  className = '',
}) {
  const displayValue =
    value != null && value !== '' ? String(value) : 'Not provided';
  const hasCopyValue = value != null && value !== '';

  return (
    <div
      className={`group flex items-start gap-3 p-3 rounded-lg transition-colors hover:bg-gray-50/80 border border-transparent ${className}`}
    >
      <div
        className={`mt-0.5 p-1.5 rounded-md shrink-0 ${
          isMissing
            ? 'bg-rose-200/50 text-rose-800'
            : isInferred
              ? 'bg-amber-200/50 text-amber-900'
              : 'bg-gray-100 text-gray-500'
        }`}
      >
        <Icon className="size-4" strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5 flex-wrap">
          <span className="text-sm font-medium text-gray-500 uppercase tracking-wide">
            {label}
          </span>
          {isInferred && !isMissing && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-amber-900 bg-amber-200/40 px-1.5 py-0.5 rounded-full border border-amber-200/60">
              <Sparkles className="size-2.5" strokeWidth={2} />
              AI Inferred
            </span>
          )}
          {isMissing && (
            <span className="inline-flex items-center gap-1 text-sm font-medium text-rose-800 bg-rose-100 px-1.5 py-0.5 rounded-full border border-rose-200/80">
              <AlertCircle className="size-2.5" strokeWidth={2} />
              Missing
            </span>
          )}
        </div>
        <p
          className={`text-sm leading-relaxed ${
            multiline ? 'whitespace-pre-wrap break-words' : 'truncate'
          } ${isMissing ? 'text-rose-800/90 italic' : 'text-gray-900'}`}
        >
          {displayValue}
        </p>
      </div>
      {hasCopyValue && (
        <CopyButton value={String(value)} className="opacity-30 group-hover:opacity-100 shrink-0" />
      )}
    </div>
  );
}

function SectionHeader({ title, icon: Icon }) {
  return (
    <div className="flex items-center gap-2 mb-3 mt-6 first:mt-0">
      <div className="p-1.5 rounded-md bg-primary-100 text-primary-800">
        <Icon size={24} strokeWidth={2} />
      </div>
      <h3 className="text-md font-semibold text-gray-900">{title}</h3>
    </div>
  );
}

function getCategoryIcon(category) {
  switch (category) {
    case 'water':
      return Droplet;
    default:
      return Wrench;
  }
}

function getPriorityConfig(priority) {
  switch (priority) {
    case 'urgent':
      return {
        label: 'Urgent',
        className: 'bg-red-600 text-white border-0',
      };
    case 'high':
      return {
        label: 'High',
        className: 'bg-orange-500 text-white border-0',
      };
    case 'medium':
      return {
        label: 'Medium',
        className: 'bg-amber-500 text-white border-0',
      };
    default:
      return {
        label: 'Normal',
        className: 'bg-gray-200 text-gray-800 border border-gray-300',
      };
  }
}

function formatDate(dateString) {
  if (!dateString) return null;
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

const MaintenanceTicketDraftModal = ({ isOpen, onClose, metaData }) => {
  const modalRef = useRef(null);
  const [isJobOrderModalOpen, setIsJobOrderModalOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setIsJobOrderModalOpen(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isJobOrderModalOpen) return;
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen, onClose, isJobOrderModalOpen]);

  const responsePartner = useMemo(
    () => (metaData?.content ? getClassifiedResponsePartner(metaData.content) : null),
    [metaData]
  );

  const jobOrder = useMemo(
    () => (responsePartner ? buildJobOrderFromMetaData(metaData) : null),
    [metaData, responsePartner]
  );

  if (!isOpen || !metaData) return null;

  const { content = {}, title, startBy, endBy } = metaData;
  const {
    category,
    priority,
    requester,
    tradeHint,
    unitLabel,
    description,
    propertyName,
    missingFields = [],
    inferredFields = [],
    locationDetails,
    permissionToEnter,
    accessInstructions,
    requestedServiceWindow,
  } = content;

  const missingList = Array.isArray(missingFields) ? missingFields : [];
  const inferredList = Array.isArray(inferredFields) ? inferredFields : [];

  const priorityConfig = getPriorityConfig(priority);
  const CategoryIcon = getCategoryIcon(category);

  const isFieldMissing = (fieldPath) => missingList.includes(fieldPath);
  const isFieldInferred = (fieldName) => inferredList.includes(fieldName);

  const startFormatted = formatDate(startBy);
  const endFormatted = formatDate(endBy);

  const tradeDisplay = tradeHint
    ? tradeHint.charAt(0).toUpperCase() + tradeHint.slice(1)
    : null;

  const stopContentMouseDown = (e) => {
    e.stopPropagation();
  };

  return (
    <>
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4 sm:p-6 animate-fade-in">
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="maintenance-draft-title"
        onMouseDown={stopContentMouseDown}
        className="bg-white sm:max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col rounded-xl shadow-2xl border border-gray-200/80"
      >
        {/* Header — card-style like reference DialogHeader */}
        <div className="p-6 pb-4 border-b border-gray-200 bg-gray-50/90 shrink-0">
          <div className="flex items-start justify-between gap-4 pr-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <span
                  className={`inline-flex items-center gap-1.5 text-sm font-semibold px-2.5 py-1 rounded-md ${priorityConfig.className}`}
                >
                  <AlertTriangle className="size-3 shrink-0" strokeWidth={2} />
                  {priorityConfig.label}
                </span>
                {category && (
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-1 rounded-md border border-gray-300 bg-white text-gray-800 capitalize">
                    <CategoryIcon className="size-3 shrink-0" strokeWidth={2} />
                    {category}
                  </span>
                )}
              </div>
              <h2
                id="maintenance-draft-title"
                className="text-xl font-semibold text-gray-900 text-balance leading-tight"
              >
                {title || 'Maintenance ticket'}
              </h2>

              {/* Legend*/}
              <div className="flex flex-wrap gap-4">
                <div className="inline-flex items-center gap-1.5 text-xs">
                  <span className="inline-flex items-center gap-1 font-medium text-amber-900 bg-amber-100 px-2 py-1 rounded-md border border-amber-200/80">
                    <Sparkles className="size-3" strokeWidth={2} />
                    AI Inferred
                  </span>
                  <span className="text-gray-500">Auto-detected from message</span>
                </div>
                <div className="inline-flex items-center gap-1.5 text-xs">
                  <span className="inline-flex items-center gap-1 font-medium text-rose-800 bg-rose-50 px-2 py-1 rounded-md border border-rose-200/80">
                    <AlertCircle className="size-3" strokeWidth={2} />
                    Missing
                  </span>
                  <span className="text-gray-500">Required information not provided</span>
                </div>
              </div>              
            </div>

            <button
              type="button"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="text-gray-400 hover:text-gray-700 hover:bg-gray-200/80 rounded-full p-1.5 transition-colors shrink-0"
              aria-label="Close"
            >
              <X className="size-6" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {responsePartner && (
            <PreferredPartnerCallout
              partner={responsePartner}
              content={content}
              onNotify={() => setIsJobOrderModalOpen(true)}
            />
          )}

          {/* Service window */}
          <div className="flex flex-wrap gap-3 p-4 bg-sky-50/90 rounded-lg border border-sky-200/80 mb-6">
            <div className="flex items-center gap-2">
              <Clock className="size-4 text-sky-900 shrink-0" strokeWidth={2} />
              <span className="text-sm font-semibold text-sky-950">Service window</span>
            </div>
            <div className="flex flex-wrap gap-4 text-sm items-center">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-gray-500">Start:</span>
                <span className="font-medium text-gray-900">{startFormatted || '—'}</span>
                {startFormatted && <CopyButton value={startFormatted} className="opacity-30 hover:opacity-100 shrink-0" />}
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-gray-500">End:</span>
                <span className="font-medium text-gray-900">{endFormatted || '—'}</span>
                {endFormatted && <CopyButton value={endFormatted} className="opacity-30 hover:opacity-100 shrink-0" />}
              </div>
            </div>
          </div>

          <SectionHeader title="Location" icon={MapPin} />
          <div className="space-y-1 mb-8">
            <FieldRow
              label="Property"
              value={propertyName}
              icon={Building2}
              isInferred={isFieldInferred('propertyName')}
            />
            <FieldRow
              label="Unit"
              value={unitLabel}
              icon={Home}
              isInferred={isFieldInferred('unitLabel')}
            />
            <FieldRow
              label="Location details"
              value={locationDetails}
              icon={MapPin}
              isInferred={isFieldInferred('locationDetails')}
            />
          </div>

          <SectionHeader title="Requester" icon={User} />
          <div className="space-y-1 mb-8">
            <FieldRow label="Name" value={requester?.name} icon={User} />
            <FieldRow label="Phone" value={requester?.phone} icon={Phone} />
            <FieldRow
              label="Email"
              value={requester?.email}
              icon={Mail}
              isMissing={isFieldMissing('requester.email')}
            />
          </div>

          <SectionHeader title="Issue details" icon={FileText} />
          <div className="space-y-1 mb-8">
            <FieldRow
              label="Description"
              value={description}
              icon={FileText}
              multiline
            />
            <FieldRow
              label="Suggested trade"
              value={tradeDisplay}
              icon={Wrench}
              isInferred={isFieldInferred('tradeHint')}
            />
            <FieldRow
              label="Requested service window"
              value={requestedServiceWindow}
              icon={Calendar}
              isInferred={isFieldInferred('requestedServiceWindow')}
            />
          </div>

          <SectionHeader title="Access information" icon={DoorOpen} />
          <div className="space-y-1 mb-8">
            <div
              className={`flex items-center gap-3 p-3 rounded-lg border ${
                !permissionToEnter
                  ? 'bg-rose-50 border-rose-200/80'
                  : 'bg-green-50 border-green-200'
              }`}
            >
              <div
                className={`p-1.5 rounded-md shrink-0 ${
                  !permissionToEnter
                    ? 'bg-rose-200/50 text-rose-800'
                    : 'bg-green-100 text-green-800'
                }`}
              >
                <DoorOpen className="size-4" strokeWidth={2} />
              </div>
              <div className="flex-1 min-w-0">
                <span className="text-sm font-medium text-gray-500 uppercase tracking-wide block">
                  Permission to enter
                </span>
                <p
                  className={`text-sm font-medium ${
                    !permissionToEnter ? 'text-rose-900' : 'text-green-800'
                  }`}
                >
                  {permissionToEnter ? 'Yes — authorized' : 'No — contact required'}
                </p>
              </div>
              <CopyButton value={permissionToEnter ? 'Yes' : 'No'} className="opacity-30 hover:opacity-100 shrink-0" />
            </div>
            <FieldRow
              label="Access instructions"
              value={accessInstructions}
              icon={FileText}
              multiline
            />
          </div>

        </div>
      </div>
    </div>

    <JobOrderReviewModal
      isOpen={isJobOrderModalOpen}
      onClose={() => setIsJobOrderModalOpen(false)}
      jobOrder={jobOrder}
    />
    </>
  );
};

export default MaintenanceTicketDraftModal;
